import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, UploadSimple, CircleNotch, CheckCircle, WarningCircle, XCircle, Image, ShieldCheck, ShieldWarning, Minus } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CodeResultRow({ item }) {
  const statusConfig = {
    PASS: { icon: CheckCircle, color: "#34C759", bg: "#34C75910" },
    FAIL: { icon: XCircle, color: "#FF5A00", bg: "#FF5A0010" },
    "N/A": { icon: Minus, color: "#404040", bg: "#F4F4F5" },
  };
  const cfg = statusConfig[item.status] || statusConfig["N/A"];
  const Icon = cfg.icon;
  return (
    <div
      data-testid={`code-result-${item.code}`}
      className="flex items-start gap-2 p-2.5 border-b border-[#E5E5E5] last:border-0"
      style={{ background: cfg.bg }}
    >
      <Icon size={16} weight="fill" style={{ color: cfg.color }} className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-mono-data text-[10px]" style={{ color: cfg.color }}>{item.status}</span>
          <span className="font-mono-data text-[10px] text-[#0A0A0A] font-bold">{item.code}</span>
        </div>
        <p className="text-xs text-[#0A0A0A] font-medium mt-0.5">{item.title}</p>
        {item.finding && <p className="text-[11px] text-[#404040] mt-0.5 leading-relaxed">{item.finding}</p>}
      </div>
    </div>
  );
}

function ViolationCard({ v }) {
  const sevColor = { CRITICAL: "#FF5A00", MAJOR: "#FFCC00", MINOR: "#002FA7" };
  return (
    <div data-testid={`violation-${v.code}`} className="wp-card border-l-2" style={{ borderLeftColor: sevColor[v.severity] || "#FF5A00" }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="font-mono-data text-[10px] px-1.5 py-0.5 rounded-sm" style={{ color: sevColor[v.severity], background: `${sevColor[v.severity]}10`, border: `1px solid ${sevColor[v.severity]}` }}>
          {v.severity}
        </span>
        <span className="font-mono-data text-[10px] font-bold">{v.code}</span>
      </div>
      <p className="text-xs text-[#0A0A0A] font-medium">{v.issue}</p>
      {v.remedy && (
        <div className="mt-1.5 p-2 bg-[#F4F4F5] rounded-sm">
          <span className="font-mono-data text-[10px] text-[#34C759]">REMEDY:</span>
          <p className="text-[11px] text-[#404040]">{v.remedy}</p>
        </div>
      )}
    </div>
  );
}

export default function InspectionPage() {
  const [trade, setTrade] = useState("electrical");
  const [jurisdiction, setJurisdiction] = useState("NEC 2023");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const jurisdictions = trade === "electrical"
    ? ["NEC 2023", "IRC 2021"]
    : ["IMC 2021", "UMC 2021", "ASHRAE 90.1"];

  useEffect(() => {
    axios.get(`${API}/inspections`).then(r => setHistory(r.data)).catch(() => {});
  }, [result]);

  const handleFile = (file) => {
    if (!file) return;
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
    setResult(null);
  };

  const handleInspect = async () => {
    if (!photo) { toast.error("Upload or capture a photo first"); return; }
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("trade", trade);
      formData.append("jurisdiction", jurisdiction);
      formData.append("description", description);
      formData.append("photo", photo);
      const res = await axios.post(`${API}/inspect`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setResult(res.data);
      toast.success("Inspection complete");
    } catch {
      toast.error("Inspection failed. Check your connection.");
    }
    setLoading(false);
  };

  const codeResults = result?.code_results || [];
  const violations = result?.violations || [];
  const passCount = codeResults.filter(c => c.status === "PASS").length;
  const failCount = codeResults.filter(c => c.status === "FAIL").length;
  const naCount = codeResults.filter(c => c.status === "N/A").length;

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="inspection-page">
      <h1 className="text-xl font-black tracking-tight mb-1" style={{ fontFamily: "'Chivo', sans-serif" }}>
        Code Inspection
      </h1>
      <p className="text-xs text-[#404040] mb-2 font-mono-data">AI-POWERED CODEBOOK COMPLIANCE CHECK</p>

      {/* Disclaimer */}
      <div className="p-2.5 bg-[#F4F4F5] border border-[#E5E5E5] rounded-sm mb-4">
        <p className="text-[10px] text-[#404040] leading-relaxed">
          <b>Disclaimer:</b> AI inspection results are for <b>reference only</b> and do not constitute 
          a certified inspection. All work must be inspected and approved by the local Authority 
          Having Jurisdiction (AHJ). WorkPro assumes no liability for code compliance decisions 
          based on AI analysis. Always verify with a licensed inspector.
        </p>
      </div>

      {/* Trade & Jurisdiction */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Select value={trade} onValueChange={v => { setTrade(v); setJurisdiction(v === "electrical" ? "NEC 2023" : "IMC 2021"); setResult(null); }}>
          <SelectTrigger data-testid="inspect-trade-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="electrical">Electrical</SelectItem>
            <SelectItem value="hvac">HVAC</SelectItem>
          </SelectContent>
        </Select>
        <Select value={jurisdiction} onValueChange={v => { setJurisdiction(v); setResult(null); }}>
          <SelectTrigger data-testid="inspect-jurisdiction-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {jurisdictions.map(j => <SelectItem key={j} value={j}>{j}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Photo Area */}
      <div className="mb-4" data-testid="photo-area">
        {preview ? (
          <div className="relative camera-hud rounded-sm overflow-hidden">
            <img src={preview} alt="Job site" className="w-full h-48 object-cover" />
            <div className="absolute top-2 right-2 flex gap-1">
              <button
                data-testid="remove-photo-btn"
                onClick={() => { setPhoto(null); setPreview(null); setResult(null); }}
                className="w-7 h-7 bg-black/60 text-white rounded-sm flex items-center justify-center hover:bg-black/80"
              >
                <XCircle size={16} />
              </button>
            </div>
            <div className="absolute bottom-2 left-2 glass-overlay px-2 py-1 rounded-sm">
              <span className="text-[10px] text-white font-mono-data">
                {trade.toUpperCase()} | {jurisdiction}
              </span>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-[#E5E5E5] rounded-sm p-8 text-center">
            <Image size={40} className="mx-auto mb-3 text-[#E5E5E5]" />
            <p className="text-sm text-[#404040] mb-3">Upload or capture a photo of the work</p>
            <div className="flex gap-2 justify-center">
              <button
                data-testid="upload-photo-btn"
                onClick={() => fileRef.current?.click()}
                className="touch-target px-4 py-2 bg-[#F4F4F5] text-[#0A0A0A] text-xs font-bold rounded-sm border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors flex items-center gap-1.5"
              >
                <UploadSimple size={14} /> Upload
              </button>
              <button
                data-testid="capture-photo-btn"
                onClick={() => cameraRef.current?.click()}
                className="touch-target px-4 py-2 bg-[#002FA7] text-white text-xs font-bold rounded-sm hover:bg-[#001D6C] transition-colors flex items-center gap-1.5"
              >
                <Camera size={14} /> Capture
              </button>
            </div>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
      </div>

      {/* Description */}
      <textarea
        data-testid="inspect-description"
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="Describe the work area or specific concerns..."
        className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] resize-none h-16 mb-4"
      />

      {/* Inspect Button */}
      <button
        data-testid="run-inspection-btn"
        onClick={handleInspect}
        disabled={loading || !photo}
        className="w-full py-3 bg-[#002FA7] text-white text-sm font-bold rounded-sm hover:bg-[#001D6C] transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
      >
        {loading ? (
          <><CircleNotch size={16} className="animate-spin" /> Analyzing against {jurisdiction}...</>
        ) : (
          <><ShieldCheck size={16} /> Run {jurisdiction} Code Inspection</>
        )}
      </button>

      {/* ── STRUCTURED RESULTS ── */}
      {result && (
        <div className="mt-4 animate-slide-up" data-testid="inspection-result">
          {/* Overall Status */}
          <div className={`wp-card mb-3 ${
            result.pass_fail === "pass" ? "border-l-4 border-l-[#34C759]" :
            result.pass_fail === "fail" ? "border-l-4 border-l-[#FF5A00]" :
            "border-l-4 border-l-[#FFCC00]"
          }`}>
            <div className="flex items-center gap-2 mb-2">
              {result.pass_fail === "pass" ? <ShieldCheck size={24} className="text-[#34C759]" weight="fill" /> :
               result.pass_fail === "fail" ? <ShieldWarning size={24} className="text-[#FF5A00]" weight="fill" /> :
               <WarningCircle size={24} className="text-[#FFCC00]" weight="fill" />}
              <div>
                <span className={`font-bold text-base uppercase ${
                  result.pass_fail === "pass" ? "text-[#34C759]" :
                  result.pass_fail === "fail" ? "text-[#FF5A00]" : "text-[#FFCC00]"
                }`}>
                  {result.pass_fail === "pass" ? "PASSED" : result.pass_fail === "fail" ? "FAILED" : "NEEDS REVIEW"}
                </span>
                <div className="font-mono-data text-[10px] text-[#404040]">
                  {result.trade?.toUpperCase()} | {result.jurisdiction || jurisdiction}
                </div>
              </div>
            </div>
            {/* Stats row */}
            {codeResults.length > 0 && (
              <div className="flex gap-3 font-mono-data text-[10px]">
                <span className="text-[#34C759]">{passCount} PASS</span>
                <span className="text-[#FF5A00]">{failCount} FAIL</span>
                <span className="text-[#404040]">{naCount} N/A</span>
              </div>
            )}
            {result.photo_description && (
              <p className="text-xs text-[#404040] mt-2 border-t border-[#E5E5E5] pt-2">{result.photo_description}</p>
            )}
          </div>

          {/* Tabbed Results */}
          {codeResults.length > 0 ? (
            <Tabs defaultValue="codes">
              <TabsList className="grid w-full grid-cols-3 h-9 mb-3">
                <TabsTrigger value="codes" className="text-xs" data-testid="inspect-codes-tab">
                  Code Results ({codeResults.length})
                </TabsTrigger>
                <TabsTrigger value="violations" className="text-xs" data-testid="inspect-violations-tab">
                  Violations {failCount > 0 && `(${failCount})`}
                </TabsTrigger>
                <TabsTrigger value="summary" className="text-xs" data-testid="inspect-summary-tab">Summary</TabsTrigger>
              </TabsList>

              <TabsContent value="codes">
                <div className="wp-card p-0 max-h-[50vh] overflow-y-auto">
                  {codeResults.map((cr, i) => <CodeResultRow key={i} item={cr} />)}
                </div>
              </TabsContent>

              <TabsContent value="violations">
                {violations.length === 0 ? (
                  <div className="wp-card text-center py-6 text-sm text-[#34C759]">
                    <CheckCircle size={24} className="mx-auto mb-2" />
                    No violations found.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {violations.map((v, i) => <ViolationCard key={i} v={v} />)}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="summary">
                <div className="wp-card">
                  <p className="text-xs text-[#404040] leading-relaxed">{result.findings}</p>
                  {result.safety_concerns?.length > 0 && (
                    <div className="mt-3 border-t border-[#E5E5E5] pt-2">
                      <span className="font-mono-data text-[10px] text-[#FF5A00]">SAFETY CONCERNS</span>
                      <ul className="mt-1 space-y-1">
                        {result.safety_concerns.map((s, i) => (
                          <li key={i} className="text-xs text-[#404040] flex items-start gap-1">
                            <WarningCircle size={10} className="text-[#FF5A00] mt-0.5 shrink-0" /> {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {result.recommendations?.length > 0 && (
                    <div className="mt-3 border-t border-[#E5E5E5] pt-2">
                      <span className="font-mono-data text-[10px] text-[#002FA7]">RECOMMENDATIONS</span>
                      <ul className="mt-1 space-y-1">
                        {result.recommendations.map((r, i) => (
                          <li key={i} className="text-xs text-[#404040]">- {r}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="wp-card">
              <div className="text-xs text-[#404040] leading-relaxed whitespace-pre-wrap">
                {result.findings}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Inspection History */}
      {history.length > 0 && !result && (
        <div className="mt-6" data-testid="inspection-history">
          <h2 className="font-mono-data text-[10px] text-[#404040] mb-2">RECENT INSPECTIONS ({history.length})</h2>
          <div className="space-y-2">
            {history.slice(0, 5).map(h => (
              <div key={h.id} className="wp-card flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium">{h.trade?.toUpperCase()}</span>
                  <span className="font-mono-data text-[10px] text-[#404040] ml-2">
                    {new Date(h.inspected_at).toLocaleDateString()}
                  </span>
                </div>
                <span className={`font-mono-data text-[10px] px-1.5 py-0.5 rounded-sm ${
                  h.pass_fail === "pass" ? "text-[#34C759] bg-[#34C75910]" :
                  h.pass_fail === "fail" ? "text-[#FF5A00] bg-[#FF5A0010]" :
                  "text-[#FFCC00] bg-[#FFCC0010]"
                }`}>
                  {h.pass_fail?.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
